import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Swal from 'sweetalert2';
import './styles.css';
import { web3 } from './utils/web3';

// Import components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import NFTGrid from './components/nft/NFTGrid';
import ListForSaleModal from './components/modals/ListForSaleModal';
import LegacyClaimsModal from './components/modals/LegacyClaimsModal';
import MintLicensesModal from './components/modals/MintLicensesModal';
import WalletSelectionModal from './components/modals/WalletSelectionModal';
import Spinner from './components/common/Spinner';
// APIStatus removed - admin-only feature, not for public frontend

// Import hooks and utilities
import { useWallet } from './hooks/useWallet';
import { useContract } from './hooks/useContract';
import { convertWeiToEther, waitForTransaction, isUserRejection } from './utils/web3';
import { CONTRACT_ADDRESSES } from './constants/contracts';

// Import ABIs
import ETCMCLICENSEV2_ABI from './ETCMCLICENSEV2_ABI.json';
import ETCPOW_ABI from './ETCPOW_ABI.json';

// Make ABIs available globally for hooks
window.ETCMCLICENSEV2_ABI = ETCMCLICENSEV2_ABI;
window.ETCPOW_ABI = ETCPOW_ABI;

// Add custom CSS to override SweetAlert styles
const style = document.createElement('style');
style.textContent = `
  .swal2-confirm {
    background-color: #2563eb !important;
    color: white !important;
    border: none !important;
    padding: 8px 24px !important;
    border-radius: 8px !important;
    font-weight: 600 !important;
    box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3) !important;
  }
  .swal2-confirm:hover {
    background-color: #1d4ed8 !important;
  }
  .swal2-cancel {
    background-color: #4b5563 !important;
    color: white !important;
    border: none !important;
    padding: 8px 24px !important;
    border-radius: 8px !important;
    font-weight: 600 !important;
  }
  .swal2-cancel:hover {
    background-color: #374151 !important;
  }
`;
document.head.appendChild(style);

const swalConfig = {
  customClass: {
    popup: '!bg-gray-900 !text-white rounded-xl p-6 !border !border-gray-700 shadow-2xl',
    title: '!text-xl !font-bold !text-green-400 mb-2',
    htmlContainer: '!text-gray-200 !text-sm',
    confirmButton: '!bg-blue-600 hover:!bg-blue-700 !text-white px-6 py-2 rounded-lg transition-colors shadow-lg !border-0 !outline-none',
    cancelButton: '!bg-gray-600 hover:!bg-gray-700 !text-white px-6 py-2 rounded-lg transition-colors shadow-lg !border-0 !outline-none',
    actions: '!gap-3 !flex !flex-row-reverse',
    denyButton: '!bg-orange-600 hover:!bg-orange-700 !text-white px-6 py-2 rounded-lg transition-colors shadow-lg !border-0 !outline-none'
  },
  buttonsStyling: false,
  backdrop: 'rgba(0, 0, 0, 0.8)',
  background: '#1f2937',
  showClass: {
    popup: 'animate__animated animate__fadeInDown'
  },
  hideClass: {
    popup: 'animate__animated animate__fadeOutUp'
  }
};

const showSwal = (options) => {
  Swal.fire({
    ...swalConfig,
    ...options
  });
};

const showHowTo = () => {
  showSwal({
    icon: 'info',
    title: 'How to Use V2 Marketplace',
    html: `
      <div style="text-align: left; max-width: 100%;">
        <!-- Getting Started Section -->
        <div style="background: rgba(22, 22, 34, 0.6); padding: 20px; border-radius: 12px; margin-bottom: 16px; border: 1px solid rgba(0, 255, 136, 0.2);">
          <p style="color: #00ff88; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; font-size: 14px;">
            <span style="margin-right: 8px; font-size: 18px;">🔗</span> Getting Started
          </p>
          <div style="space-y: 8px;">
            <div style="display: flex; align-items: start; gap: 12px; margin-bottom: 10px;">
              <span style="color: #00ff88; font-weight: 600; min-width: 24px;">1️⃣</span>
              <p style="color: #ffffff; margin: 0; font-size: 13px; line-height: 1.6;">
                Connect your Wallet using the <span style="color: #00ff88; font-weight: 600;">Connect Wallet</span> button
              </p>
            </div>
            <div style="display: flex; align-items: start; gap: 12px; margin-bottom: 10px;">
              <span style="color: #00ff88; font-weight: 600; min-width: 24px;">2️⃣</span>
              <p style="color: #ffffff; margin: 0; font-size: 13px; line-height: 1.6;">
                Click <span style="color: #00ff88; font-weight: 600;">Approve</span> to approve ETCPOWv2 for spending
              </p>
            </div>
            <div style="background: rgba(245, 158, 11, 0.1); padding: 10px; border-radius: 8px; border: 1px solid rgba(245, 158, 11, 0.2); margin-top: 8px;">
              <p style="color: #fbbf24; font-size: 12px; margin: 0; display: flex; align-items: center; gap: 6px;">
                <span>⚠️</span>
                <span>Approve at least the amount you plan to spend</span>
              </p>
            </div>
          </div>
        </div>

        <!-- License Duration & Renewal Section -->
        <div style="background: rgba(34, 197, 94, 0.1); padding: 20px; border-radius: 12px; margin-bottom: 16px; border: 1px solid rgba(34, 197, 94, 0.3);">
          <p style="color: #22c55e; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; font-size: 14px;">
            <span style="margin-right: 8px; font-size: 18px;">📅</span> License Duration & Renewal
          </p>
          <div style="space-y: 8px;">
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • <span style="color: #22c55e; font-weight: 600;">New licenses last 12 months</span>
            </p>
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • <span style="color: #22c55e; font-weight: 600;">Maximum duration: 18 months</span> total (original + renewals)
            </p>
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • <span style="color: #22c55e; font-weight: 600;">Renewals add 180 days</span> (or less if near 18-month max)
            </p>
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • <span style="color: #22c55e; font-weight: 600;">Renewal cost: 50%</span> of current public mint price
            </p>
            <div style="background: rgba(34, 197, 94, 0.1); padding: 10px; border-radius: 8px; border: 1px solid rgba(34, 197, 94, 0.2); margin-top: 8px;">
              <p style="color: #22c55e; font-size: 12px; margin: 0; font-weight: 600;">
                💡 Look for the <span style="color: #22c55e;">🔄 Renew</span> button on your owned licenses
              </p>
            </div>
          </div>
        </div>

        <!-- License Expiration Section -->
        <div style="background: rgba(245, 158, 11, 0.1); padding: 20px; border-radius: 12px; margin-bottom: 16px; border: 1px solid rgba(245, 158, 11, 0.3);">
          <p style="color: #f59e0b; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; font-size: 14px;">
            <span style="margin-right: 8px; font-size: 18px;">⏰</span> License Expiration
          </p>
          <div style="space-y: 8px;">
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • Expired licenses <span style="color: #ef4444; font-weight: 600;">cannot be used or listed for sale</span>
            </p>
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • Need <span style="color: #fbbf24; font-weight: 600;">35+ days remaining</span> to list for sale
            </p>
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • <span style="color: #fbbf24; font-weight: 600;">Lifetime licenses</span> never expire and cannot be renewed
            </p>
            <p style="color: #ffffff; margin: 0; font-size: 13px; line-height: 1.6;">
              • Expired licenses are automatically burned to clean up the contract
            </p>
          </div>
        </div>

        <!-- Legacy Claims Section -->
        <div style="background: rgba(22, 22, 34, 0.6); padding: 20px; border-radius: 12px; margin-bottom: 16px; border: 1px solid rgba(245, 158, 11, 0.2);">
          <p style="color: #f59e0b; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; font-size: 14px;">
            <span style="margin-right: 8px; font-size: 18px;">🎫</span> Legacy Claims
          </p>
          <div style="space-y: 8px;">
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • If you have legacy entitlements, use <span style="color: #f59e0b; font-weight: 600;">Claim Legacy</span>
            </p>
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • Legacy claims also give <span style="color: #22c55e; font-weight: 600;">12-month licenses</span>
            </p>
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • Pay the claim price in ETCPOWv2 to receive licenses
            </p>
            <div style="background: rgba(245, 158, 11, 0.1); padding: 10px; border-radius: 8px; border: 1px solid rgba(245, 158, 11, 0.2);">
              <p style="color: #fbbf24; font-size: 12px; margin: 0; font-weight: 600;">
                ⚠️ You need ETCPOWv2 tokens to claim licenses
              </p>
            </div>
          </div>
        </div>

        <!-- Public Minting Section -->
        <div style="background: rgba(22, 22, 34, 0.6); padding: 20px; border-radius: 12px; margin-bottom: 16px; border: 1px solid rgba(168, 85, 247, 0.2);">
          <p style="color: #a855f7; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; font-size: 14px;">
            <span style="margin-right: 8px; font-size: 18px;">🪙</span> Public License Minting
          </p>
          <div style="space-y: 8px;">
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • Use <span style="color: #a855f7; font-weight: 600;">Mint License</span> to purchase fresh 12-month licenses
            </p>
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • Available when enabled by contract admin
            </p>
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • Different tiers may have different prices and features
            </p>
          </div>
        </div>

        <!-- Trading Section -->
        <div style="background: rgba(22, 22, 34, 0.6); padding: 20px; border-radius: 12px; margin-bottom: 16px; border: 1px solid rgba(59, 130, 246, 0.2);">
          <p style="color: #3b82f6; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; font-size: 14px;">
            <span style="margin-right: 8px; font-size: 18px;">🛒</span> Trading
          </p>
          <div style="space-y: 8px;">
            <div style="display: flex; align-items: start; gap: 12px; margin-bottom: 10px;">
              <span style="color: #3b82f6; font-weight: 600; min-width: 24px;">3️⃣</span>
              <p style="color: #ffffff; margin: 0; font-size: 13px; line-height: 1.6;">
                Browse listed NFTs and press <span style="color: #3b82f6; font-weight: 600;">Buy</span> to purchase
              </p>
            </div>
            <div style="display: flex; align-items: start; gap: 12px; margin-bottom: 10px;">
              <span style="color: #3b82f6; font-weight: 600; min-width: 24px;">4️⃣</span>
              <p style="color: #ffffff; margin: 0; font-size: 13px; line-height: 1.6;">
                Press <span style="color: #3b82f6; font-weight: 600;">View My NFTs</span> to manage your licenses
              </p>
            </div>
            <div style="display: flex; align-items: start; gap: 12px;">
              <span style="color: #3b82f6; font-weight: 600; min-width: 24px;">5️⃣</span>
              <p style="color: #ffffff; margin: 0; font-size: 13px; line-height: 1.6;">
                Set your price when listing: <span style="color: #3b82f6; font-weight: 600;">1</span> = 1 ETCPOWv2
              </p>
            </div>
          </div>
        </div>

        <!-- Important Section -->
        <div style="background: rgba(22, 22, 34, 0.6); padding: 20px; border-radius: 12px; margin-bottom: 16px; border: 1px solid rgba(239, 68, 68, 0.2);">
          <p style="color: #ef4444; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; font-size: 14px;">
            <span style="margin-right: 8px; font-size: 18px;">⚠️</span> Important
          </p>
          <div style="space-y: 8px;">
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • Listed NFTs can't be cancelled for <span style="color: #ef4444; font-weight: 600;">30 days</span>
            </p>
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • <span style="color: #ef4444; font-weight: 600;">5% marketplace fee</span> deducted from sales
            </p>
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • Always double-check prices before transactions
            </p>
            <p style="color: #ffffff; margin: 0 0 8px 0; font-size: 13px; line-height: 1.6;">
              • Renewals can be <span style="color: #ef4444; font-weight: 600;">disabled</span> by contract admin
            </p>
          </div>
        </div>

        <!-- Footer Features -->
        <div style="text-align: center; margin-top: 24px; padding-top: 20px; border-top: 1px solid rgba(255, 255, 255, 0.1);">
          <p style="color: #a0a0b8; font-size: 12px; margin: 0;">
            V2 Features: <span style="color: #22c55e;">12-Month Licenses</span> • <span style="color: #22c55e;">Renewals</span> • <span style="color: #00ff88;">Tier System</span> • <span style="color: #00ff88;">Claims</span> • <span style="color: #00ff88;">Minting</span> • <span style="color: #00ff88;">Trading</span>
          </p>
        </div>
      </div>
    `,
    confirmButtonText: 'Got it!',
    width: '650px',
    customClass: {
      popup: '!bg-dark-card !text-white rounded-2xl p-6 !border !border-dark-border shadow-2xl',
      title: '!text-xl !font-bold !text-etc-primary mb-2',
      htmlContainer: '!text-text-primary !text-sm',
      confirmButton: '!bg-etc-primary hover:!bg-etc-secondary !text-white px-6 py-2.5 rounded-lg transition-colors shadow-lg !border-0 !outline-none !font-medium',
    }
  });
};

function App() {
  // Wallet state
  const { account, isConnected, isConnecting, connectWallet, disconnect } = useWallet();
  
  // App state
  const [nfts, setNFTs] = useState([]);
  const [mode, setMode] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [mintModalOpen, setMintModalOpen] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [priceInput, setPriceInput] = useState('');
  const [selectedNFTId, setSelectedNFTId] = useState(null);
  const [minListingPrice, setMinListingPrice] = useState('0');
  const [claimAmount, setClaimAmount] = useState('');
  const [mintAmount, setMintAmount] = useState('');
  const [legacyInfo, setLegacyInfo] = useState({ entitled: '0', claimed: '0', remaining: '0' });
  const [legacyClaimPrice, setLegacyClaimPrice] = useState('0');
  const [etcPowBalance, setEtcPowBalance] = useState('0');
  const [publicPrices, setPublicPrices] = useState({ 1: '0.00', 2: '0.00' });
  const [featureFlags, setFeatureFlags] = useState({ legacyEnabled: false, publicEnabled: false });
  // Initialize tier enabled with false, but preserve once loaded
  const [tierEnabled, setTierEnabled] = useState({ 1: false, 2: false });
  const tierEnabledLoadedRef = useRef(false);

  // Contract hook
  const { 
    contract,
    getNFTs, 
    getMyNFTs, 
    getPublicPrices: fetchPublicPrices, 
    getLegacyInfo,
    getLegacyClaimPrice,
    getETCPOWBalance,
    getFeatureFlags,
    getTierEnabled,
    getMinListingPrice,
    approveETCPOW, 
    buyNFT, 
    listNFTForSale, 
    cancelListing,
    claimLegacyLicenses, 
    publicMintLicenses,
    renewLicense,
    isLoading,
    triggerSync,
    triggerFullSync
    // API status functions removed - admin-only feature
  } = useContract(account);

  // Define data loading functions before useEffect hooks that use them
  const loadBlockchainData = useCallback(async () => {
    try {
      const nftsPromise = mode === 'My' ? getMyNFTs() : getNFTs();
      const [loadedNFTs, legacyData, claimPrice, etcPowBalance] = await Promise.all([
        nftsPromise,
        getLegacyInfo(),
        getLegacyClaimPrice(),
        getETCPOWBalance(),
      ]);
      
      // Always update NFTs - use requestAnimationFrame to batch updates and prevent flickering
      requestAnimationFrame(() => {
        setNFTs(loadedNFTs);
      });
      setLegacyInfo(legacyData);
      setLegacyClaimPrice(claimPrice);
      setEtcPowBalance(etcPowBalance);
      
      // NFTs loaded successfully
    } catch (error) {
      console.error('Error loading blockchain data:', error);
      // Only log errors, don't show warnings to users for API issues
      // API errors are handled gracefully by the API hook
    }
  }, [mode, getMyNFTs, getNFTs, getLegacyInfo, getLegacyClaimPrice, getETCPOWBalance]);

  const loadNFTsOnly = useCallback(async () => {
    try {
      const loadedNFTs = mode === 'My' ? await getMyNFTs() : await getNFTs();
      // Always update when mode changes or when data actually changes
      // Use requestAnimationFrame to batch state updates and prevent flickering
      requestAnimationFrame(() => {
        setNFTs(loadedNFTs);
      });
    } catch (error) {
      console.error('Error loading NFTs:', error);
      // Silently fail for NFT-only loads to avoid UI spam
    }
  }, [mode, getMyNFTs, getNFTs]);

  // Load data on mount and when account changes (with debouncing to prevent spam)
  useEffect(() => {
    if (!isConnected || !account || !contract) return;
    
    // Debounce to prevent rapid-fire API calls
    const timer = setTimeout(() => {
      loadBlockchainData();
    }, 300); // 300ms debounce
    
    return () => clearTimeout(timer);
  }, [isConnected, account, contract, loadBlockchainData]);

  // Reload NFTs when switching between All/My (with debouncing)
  useEffect(() => {
    if (!isConnected || !contract) return;
    
    // Debounce to prevent rapid-fire API calls
    const timer = setTimeout(() => {
      loadNFTsOnly();
    }, 300); // 300ms debounce
    
    return () => clearTimeout(timer);
  }, [mode, isConnected, contract, loadNFTsOnly]);

  const handleConnectWallet = () => {
    setWalletModalOpen(true);
  };

  const handleSelectWallet = async (walletType) => {
    setWalletModalOpen(false);
    try {
      await connectWallet(walletType);
    } catch (error) {
      if (walletType === 'metamask' && error.message === 'MetaMask is not installed') {
        showSwal({
          icon: 'error',
          title: '🦊 MetaMask Not Found',
          html: `
            <div class="text-left">
              <p class="text-gray-200 mb-4">MetaMask wallet is required to use this marketplace.</p>
              <div class="bg-gray-800 rounded-lg p-4 mb-4">
                <p class="text-orange-400 font-semibold mb-2">📥 To get started:</p>
                <ol class="text-gray-300 space-y-2 text-sm">
                  <li>1. Install MetaMask browser extension</li>
                  <li>2. Create or import your wallet</li>
                  <li>3. Refresh this page and try again</li>
                </ol>
              </div>
              <div class="text-center">
                <a href="https://metamask.io/download/" target="_blank" class="inline-block bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg transition-colors no-underline">
                  Download MetaMask
                </a>
              </div>
            </div>
          `,
          confirmButtonText: 'Close',
          showCancelButton: false
        });
      } else {
        showSwal({
          icon: 'error',
          title: 'Connection Failed',
          text: error.message || 'Failed to connect wallet. Please try again.',
          confirmButtonText: 'Close'
        });
      }
    }
  };

  const loadPricesForMintModal = useCallback(async () => {
    try {
      const prices = await fetchPublicPrices();
      setPublicPrices(prices);
    } catch (error) {
      console.error('Error loading prices for mint modal:', error);
    }
  }, [fetchPublicPrices]);

  // Track if we've loaded tier status and feature flags (use ref to persist across renders)
  const tierStatusLoadedRef = useRef(false);

  // Load tier enabled status and feature flags (wait for contract)
  // Only load once when contract becomes available, not on every render
  useEffect(() => {
    if (!isConnected || !contract || tierStatusLoadedRef.current) return;

    let isMounted = true;

    const loadTierStatus = async () => {
      try {
        const tier1Enabled = await getTierEnabled(1);
        const tier2Enabled = await getTierEnabled(2);

        if (isMounted) {
          tierEnabledLoadedRef.current = true;
          setTierEnabled({
            1: tier1Enabled,
            2: tier2Enabled
          });
        }
      } catch (error) {
        console.error('Error loading tier status:', error);
      }
    };

    const loadFeatureFlags = async () => {
      try {
        const flags = await getFeatureFlags();
        if (isMounted) {
          setFeatureFlags(flags);
        }
      } catch (error) {
        console.error('Error loading feature flags:', error);
      }
    };

    // Mark as loaded and fetch data
    tierStatusLoadedRef.current = true;
    loadTierStatus();
    loadFeatureFlags();

    return () => {
      isMounted = false;
    };
  }, [isConnected, contract]); // Removed function dependencies to prevent infinite loops

  // Fetch prices when mint modal opens
  useEffect(() => {
    if (mintModalOpen && isConnected && contract) {
      loadPricesForMintModal();
    }
  }, [mintModalOpen, isConnected, contract, fetchPublicPrices]);

  const toggleMode = () => {
    setMode(mode === 'All' ? 'My' : 'All');
  };

  // Memoize sort functions to prevent unnecessary recalculations
  const sortByPriceAscending = useCallback(() => {
    setNFTs(prev => {
      const sorted = [...prev].sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0));
      return sorted;
    });
  }, []);

  const sortByPriceDescending = useCallback(() => {
    setNFTs(prev => {
      const sorted = [...prev].sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0));
      return sorted;
    });
  }, []);

  const requestApproval = async () => {
    try {
      const ridiculousPrice = '199999999999999999999999999999999';
      await approveETCPOW(ridiculousPrice);
      showSwal({
        icon: 'success',
        title: 'Approval Successful!',
        text: 'You can now spend ETCPOWv2 tokens in the marketplace.',
      });
    } catch (error) {
      console.error('Error requesting approval:', error);
      const errCode = error?.code;
      const errMsg = (error?.message || '').toLowerCase();
      const isUserRejected =
        errCode === 4001 ||
        errCode === 'ACTION_REJECTED' ||
        errMsg.includes('user rejected') ||
        errMsg.includes('user denied') ||
        errMsg.includes('rejected the request');

      if (isUserRejected) {
        showSwal({
          icon: 'info',
          title: 'Approval Cancelled',
          text: 'You cancelled the approval in your wallet.',
        });
        return;
      }
      showSwal({
        icon: 'error',
        title: 'Approval Failed',
        text: 'Failed to approve ETCPOWv2 tokens. Please try again.',
      });
    }
  };

  const handleBuyNFT = async (tokenId) => {
    try {
      // First, check if user has enough ETCPOWv2 balance
      const nft = nfts.find(n => n.tokenId === tokenId.toString());
      if (!nft) {
        throw new Error('NFT not found');
      }

      const price = nft.price;
      const balance = await getETCPOWBalance();
      
      if (parseFloat(balance) < parseFloat(price)) {
        showSwal({
          icon: 'error',
          title: 'Insufficient Balance',
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; margin-bottom: 16px;">💰</div>
              <div style="font-size: 20px; font-weight: bold; color: #ef4444; margin-bottom: 8px;">
                Insufficient ETCPOWv2 Balance
              </div>
              <div style="font-size: 16px; margin-bottom: 12px;">
                You need ${price} ETCPOWv2 but only have ${balance} ETCPOWv2
              </div>
            </div>
          `,
        });
        return;
      }

      // Show loading for approval
      Swal.fire({
        ...swalConfig,
        title: '⏳ Approving Tokens',
        html: `
          <div style="text-align: center;">
            <div class="swal2-loader" style="border: 4px solid rgba(59, 130, 246, 0.3); border-top-color: #3b82f6; border-radius: 50%; width: 48px; height: 48px; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
            <p style="color: #9ca3af; margin: 0;">Please approve ETCPOWv2 tokens in your wallet...</p>
          </div>
        `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Check if user has approved ETCPOWv2 for marketplace
      const ridiculousPrice = '199999999999999999999999999999999999';
      try {
        await approveETCPOW(ridiculousPrice);
      } catch (approvalError) {
        if (isUserRejection(approvalError)) {
          Swal.fire({
            ...swalConfig,
            icon: 'info',
            title: 'Approval Cancelled',
            html: `
              <div style="text-align: center;">
                <div style="font-size: 48px; margin-bottom: 16px;">🚫</div>
                <p style="color: #9ca3af; margin: 0;">You cancelled the approval in your wallet.</p>
              </div>
            `,
            confirmButtonText: 'OK'
          });
          return;
        }
        throw approvalError;
      }

      // Update to purchase loading
      Swal.update({
        title: '⏳ Processing Purchase',
        html: `
          <div style="text-align: center;">
            <div class="swal2-loader" style="border: 4px solid rgba(16, 185, 129, 0.3); border-top-color: #10b981; border-radius: 50%; width: 48px; height: 48px; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
            <p style="color: #9ca3af; margin: 0;">Please confirm the purchase in your wallet...</p>
          </div>
        `
      });

      // Now proceed with purchase
      const txHash = await buyNFT(tokenId);
      
      // Update loading message with tx hash
      Swal.update({
        title: '⏳ Waiting for Confirmation',
        html: `
          <div style="text-align: center;">
            <div class="swal2-loader" style="border: 4px solid rgba(16, 185, 129, 0.3); border-top-color: #10b981; border-radius: 50%; width: 48px; height: 48px; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
            <p style="color: #9ca3af; margin: 0 0 12px 0;">Transaction submitted! Waiting for confirmation...</p>
            <div style="font-size: 11px; color: #6b7280; word-break: break-all; background: rgba(255, 255, 255, 0.1); padding: 8px; border-radius: 6px; margin-top: 8px;">
              ${txHash}
            </div>
          </div>
        `
      });

      // Wait for transaction confirmation
      // If confirmation check fails, we still show success since transaction was submitted
      let receipt = null;
      try {
        receipt = await waitForTransaction(txHash, 1, 180000); // 3 minutes timeout
      } catch (waitError) {
        // If waiting fails but we have a tx hash, transaction was still submitted
        // This can happen if RPC is slow or transaction takes longer than expected
        // We'll show success with a note that confirmation is pending
        console.warn('Transaction confirmation check issue, but transaction was submitted:', txHash);
      }

      // Show success
      Swal.fire({
        ...swalConfig,
        icon: 'success',
        title: '🎉 Purchase Successful!',
        html: `
          <div style="text-align: center;">
            <div style="font-size: 48px; margin-bottom: 16px;">🛒</div>
            <div style="font-size: 20px; font-weight: bold; color: #10b981; margin-bottom: 8px;">
              Purchase Successful!
            </div>
            <div style="font-size: 16px; margin-bottom: 12px; color: #d1d5db;">
              Successfully purchased NFT #${tokenId} for ${price} ETCPOWv2
            </div>
            <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; margin-top: 16px;">
              Transaction Hash:
            </div>
            <div style="font-size: 11px; color: #9ca3af; word-break: break-all; background: rgba(255, 255, 255, 0.1); padding: 8px; border-radius: 6px;">
              ${txHash}
            </div>
          </div>
        `,
        confirmButtonText: 'Awesome!',
        confirmButtonColor: '#10b981'
      });
      await loadBlockchainData();
    } catch (error) {
      console.error('Error buying NFT:', error);
      
      // Check if user rejected
      if (isUserRejection(error)) {
        Swal.fire({
          ...swalConfig,
          icon: 'info',
          title: 'Transaction Cancelled',
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; margin-bottom: 16px;">🚫</div>
              <p style="color: #9ca3af; margin: 0;">You cancelled the transaction in your wallet.</p>
            </div>
          `,
          confirmButtonText: 'OK'
        });
      } else if (error.message && error.message.includes('transfer to zero address')) {
        Swal.fire({
          ...swalConfig,
          icon: 'error',
          title: 'Approval Required',
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; margin-bottom: 16px;">🔐</div>
              <div style="font-size: 20px; font-weight: bold; color: #ef4444; margin-bottom: 8px;">
                Token Approval Failed
              </div>
              <div style="font-size: 16px; margin-bottom: 12px; color: #d1d5db;">
                Please approve ETCPOWv2 tokens first, then try again.
              </div>
              <div style="font-size: 14px; color: #6b7280;">
                Click "Request Approval" button in the header to approve tokens.
              </div>
            </div>
          `,
          confirmButtonText: 'OK'
        });
      } else {
        Swal.fire({
          ...swalConfig,
          icon: 'error',
          title: '❌ Purchase Failed',
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
              <p style="color: #ef4444; margin: 0 0 8px 0; font-weight: 600;">Transaction Failed</p>
              <p style="color: #9ca3af; margin: 0; font-size: 14px;">${error.message || 'Failed to purchase NFT. Please try again.'}</p>
            </div>
          `,
          confirmButtonText: 'Try Again'
        });
      }
    }
  };

  const handleListForSale = async (nftId) => {
    setSelectedNFTId(nftId);
    
    // Find the NFT to get its tier
    const nft = nfts.find(n => n.tokenId?.toString() === nftId.toString() || n.id?.toString() === nftId.toString());
    if (nft) {
      try {
        const minPrice = await getMinListingPrice(nft.tier || 1);
        setMinListingPrice(minPrice);
      } catch (error) {
        console.error('Error fetching min listing price:', error);
        setMinListingPrice('0');
      }
    } else {
      setMinListingPrice('0');
    }
    
    setIsModalOpen(true);
  };

  const handleViewDetails = (nft) => {
    // Extract license type from metadata attributes
    const getLicenseType = () => {
      if (nft.attributes && Array.isArray(nft.attributes)) {
        const tierAttribute = nft.attributes.find(attr => 
          attr.trait_type === 'Tier' || attr.trait_type === 'tier'
        );
        return tierAttribute ? tierAttribute.value : 'Normal';
      }
      return nft.tier === 2 ? 'Pro' : 'Normal';
    };

    const licenseType = getLicenseType();
    const isPro = licenseType === 'Pro';
    
    // Get creator from attributes
    const getCreator = () => {
      if (nft.attributes && Array.isArray(nft.attributes)) {
        const creatorAttr = nft.attributes.find(attr => 
          attr.trait_type === 'Creator' || attr.trait_type === 'creator'
        );
        return creatorAttr ? creatorAttr.value : 'ETCMC';
      }
      return 'ETCMC';
    };

    const getYearCreated = () => {
      if (nft.attributes && Array.isArray(nft.attributes)) {
        const yearAttr = nft.attributes.find(attr => 
          attr.trait_type === 'Year Created' || attr.trait_type === 'year created'
        );
        return yearAttr ? yearAttr.value : '2026';
      }
      return '2026';
    };

    // Determine if NFT is listed based on price
    const isListed = nft.price && nft.price !== '0';
    const priceInEther = isListed ? nft.price : '0'; // Already converted in useContract.js

    const getLicenseBadgeStyle = () => {
      return isPro 
        ? 'background: linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.2)); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.3);' 
        : 'background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(30, 64, 175, 0.2)); color: #93c5fd; border: 1px solid rgba(59, 130, 246, 0.3);';
    };

    const getLicenseIcon = () => isPro ? '⭐' : '🛡️';

    const getMetadataSection = () => {
      if (!nft.tokenURI) return '';
      return `
        <div style="background: rgba(22, 22, 34, 0.6); padding: 20px; border-radius: 12px; border: 1px solid rgba(255, 255, 255, 0.1);">
          <p style="color: #00ff88; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; font-size: 14px;">
            <span style="margin-right: 8px; font-size: 16px;">🔗</span> Metadata
          </p>
          <div style="background: rgba(0, 0, 0, 0.4); padding: 12px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.1);">
            <p style="color: #a0a0b8; font-size: 11px; word-break: break-all; font-family: 'Courier New', monospace; margin: 0; line-height: 1.5;">
              ${nft.tokenURI}
            </p>
          </div>
        </div>
      `;
    };

    showSwal({
      icon: 'info',
      title: `${licenseType} License #${nft.tokenId}`,
      html: `
        <div style="text-align: left; max-width: 100%;">
          <!-- License Type Badge -->
          <div style="text-align: center; margin-bottom: 24px;">
            <div style="display: inline-flex; align-items: center; padding: 10px 20px; border-radius: 12px; font-weight: 600; font-size: 14px; ${getLicenseBadgeStyle()}">
              <span style="margin-right: 8px; font-size: 16px;">${getLicenseIcon()}</span>
              ${licenseType} License
            </div>
          </div>

          <!-- Token Information -->
          <div style="background: rgba(22, 22, 34, 0.6); padding: 20px; border-radius: 12px; margin-bottom: 16px; border: 1px solid rgba(255, 255, 255, 0.1);">
            <p style="color: #00ff88; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; font-size: 14px;">
              <span style="margin-right: 8px; font-size: 16px;">🆔</span> ID Token Information
            </p>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; font-size: 13px;">
              <div>
                <p style="color: #a0a0b8; margin: 0 0 4px 0; font-size: 12px;">Token ID:</p>
                <p style="color: #ffffff; font-weight: 500; margin: 0;">#${nft.tokenId}</p>
              </div>
              <div>
                <p style="color: #a0a0b8; margin: 0 0 4px 0; font-size: 12px;">Name:</p>
                <p style="color: #ffffff; font-weight: 500; margin: 0;">${nft.name || 'ETCMC-License-NFT-V2'}</p>
              </div>
              <div>
                <p style="color: #a0a0b8; margin: 0 0 4px 0; font-size: 12px;">Contract Tier:</p>
                <p style="color: #ffffff; font-weight: 500; margin: 0;">${licenseType}</p>
              </div>
              <div>
                <p style="color: #a0a0b8; margin: 0 0 4px 0; font-size: 12px;">Creator:</p>
                <p style="color: #ffffff; font-weight: 500; margin: 0;">${getCreator()}</p>
              </div>
            </div>
          </div>

          <!-- Additional Information -->
          <div style="background: rgba(22, 22, 34, 0.6); padding: 20px; border-radius: 12px; margin-bottom: 16px; border: 1px solid rgba(255, 255, 255, 0.1);">
            <p style="color: #00ff88; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; font-size: 14px;">
              <span style="margin-right: 8px; font-size: 16px;">📋</span> Additional Information
            </p>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; font-size: 13px;">
              <div>
                <p style="color: #a0a0b8; margin: 0 0 4px 0; font-size: 12px;">Year Created:</p>
                <p style="color: #ffffff; font-weight: 500; margin: 0;">${getYearCreated()}</p>
              </div>
              <div>
                <p style="color: #a0a0b8; margin: 0 0 4px 0; font-size: 12px;">Listed:</p>
                <p style="color: ${isListed ? '#10b981' : '#ef4444'}; font-weight: 500; margin: 0;">${isListed ? 'Yes' : 'No'}</p>
              </div>
            </div>
            ${isListed ? `
              <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid rgba(255, 255, 255, 0.1);">
                <p style="color: #a0a0b8; margin: 0 0 4px 0; font-size: 12px;">Listing Price:</p>
                <p style="color: #10b981; font-weight: 600; margin: 0; font-size: 16px;">${priceInEther} ETCPOW</p>
              </div>
            ` : ''}
          </div>

          <!-- Description -->
          ${nft.description ? `
            <div style="background: rgba(22, 22, 34, 0.6); padding: 20px; border-radius: 12px; margin-bottom: 16px; border: 1px solid rgba(255, 255, 255, 0.1);">
              <p style="color: #00ff88; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; font-size: 14px;">
                <span style="margin-right: 8px; font-size: 16px;">📝</span> Description
              </p>
              <p style="color: #d1d5db; margin: 0; line-height: 1.6; font-size: 13px;">${nft.description}</p>
            </div>
          ` : ''}

          <!-- Metadata -->
          ${getMetadataSection()}
        </div>
      `,
      confirmButtonText: 'Close',
      confirmButtonColor: '#00ff88',
      width: '600px',
      customClass: {
        popup: '!bg-dark-card !text-white rounded-2xl p-6 !border !border-dark-border shadow-2xl',
        title: '!text-xl !font-bold !text-etc-primary mb-2',
        htmlContainer: '!text-text-primary !text-sm',
        confirmButton: '!bg-etc-primary hover:!bg-etc-secondary !text-white px-6 py-2.5 rounded-lg transition-colors shadow-lg !border-0 !outline-none !font-medium',
      }
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setPriceInput('');
    setSelectedNFTId(null);
  };

  const handleRenewSuccess = (txHash, renewedNft) => {
    // Show success notification
    showSwal({
      icon: 'success',
      title: '🔄 License Renewed!',
      html: `
        <div style="text-align: center;">
          <div style="font-size: 48px; margin-bottom: 16px;">✅</div>
          <div style="font-size: 20px; font-weight: bold; color: #10b981; margin-bottom: 8px;">
            License Renewed Successfully!
          </div>
          <div style="font-size: 16px; margin-bottom: 12px; color: #d1d5db;">
            Your license #${renewedNft.tokenId} has been renewed for 180 days
          </div>
          <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; margin-top: 16px;">
            Transaction: ${txHash}
          </div>
        </div>
      `,
      confirmButtonText: 'Great!',
      confirmButton: '!bg-green-600 hover:!bg-green-700 !text-white px-6 py-2.5 rounded-lg transition-colors shadow-lg !border-0 !outline-none !font-medium',
    });

    // Refresh NFT data to show updated expiration
    setTimeout(() => {
      if (triggerSync) {
        triggerSync();
      }
    }, 2000);
  };

  const handleCancelListing = async (tokenId) => {
    // Show loading state
    Swal.fire({
      ...swalConfig,
      title: '⏳ Processing Transaction',
      html: `
        <div style="text-align: center;">
          <div class="swal2-loader" style="border: 4px solid rgba(239, 68, 68, 0.3); border-top-color: #ef4444; border-radius: 50%; width: 48px; height: 48px; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
          <p style="color: #9ca3af; margin: 0;">Please confirm the transaction in your wallet...</p>
        </div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const txHash = await cancelListing(tokenId);
      
      // Update loading message with tx hash
      Swal.update({
        title: '⏳ Waiting for Confirmation',
        html: `
          <div style="text-align: center;">
            <div class="swal2-loader" style="border: 4px solid rgba(16, 185, 129, 0.3); border-top-color: #10b981; border-radius: 50%; width: 48px; height: 48px; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
            <p style="color: #9ca3af; margin: 0 0 12px 0;">Transaction submitted! Waiting for confirmation...</p>
            <div style="font-size: 11px; color: #6b7280; word-break: break-all; background: rgba(255, 255, 255, 0.1); padding: 8px; border-radius: 6px; margin-top: 8px;">
              ${txHash}
            </div>
          </div>
        `
      });

      // Wait for transaction confirmation
      // If confirmation check fails, we still show success since transaction was submitted
      let receipt = null;
      try {
        receipt = await waitForTransaction(txHash, 1, 180000); // 3 minutes timeout
      } catch (waitError) {
        // If waiting fails but we have a tx hash, transaction was still submitted
        // This can happen if RPC is slow or transaction takes longer than expected
        // We'll show success with a note that confirmation is pending
        console.warn('Transaction confirmation check issue, but transaction was submitted:', txHash);
      }

      // Show success
      Swal.fire({
        ...swalConfig,
        icon: 'success',
        title: '🚫 Listing Cancelled!',
        html: `
          <div style="text-align: center;">
            <div style="font-size: 48px; margin-bottom: 16px;">✅</div>
            <div style="font-size: 20px; font-weight: bold; color: #10b981; margin-bottom: 8px;">
              Listing Cancelled!
            </div>
            <div style="font-size: 16px; margin-bottom: 12px; color: #d1d5db;">
              Your NFT #${tokenId} is no longer listed for sale
            </div>
            <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; margin-top: 16px;">
              Transaction Hash:
            </div>
            <div style="font-size: 11px; color: #9ca3af; word-break: break-all; background: rgba(255, 255, 255, 0.1); padding: 8px; border-radius: 6px;">
              ${txHash}
            </div>
          </div>
        `,
        confirmButtonText: 'Got it!',
        confirmButtonColor: '#10b981'
      });
      await loadBlockchainData();
    } catch (error) {
      console.error('Error canceling listing:', error);
      
      if (isUserRejection(error)) {
        Swal.fire({
          ...swalConfig,
          icon: 'info',
          title: 'Transaction Cancelled',
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; margin-bottom: 16px;">🚫</div>
              <p style="color: #9ca3af; margin: 0;">You cancelled the transaction in your wallet.</p>
            </div>
          `,
          confirmButtonText: 'OK'
        });
      } else {
        Swal.fire({
          ...swalConfig,
          icon: 'error',
          title: '❌ Cancel Failed',
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
              <p style="color: #ef4444; margin: 0 0 8px 0; font-weight: 600;">Transaction Failed</p>
              <p style="color: #9ca3af; margin: 0; font-size: 14px;">${error.message || 'Failed to cancel listing. Please try again.'}</p>
            </div>
          `,
          confirmButtonText: 'Try Again'
        });
      }
    }
  };

  const handleSubmitPrice = async () => {
    if (!priceInput || Number(priceInput) <= 0) return;
    
    if (!selectedNFTId) {
      Swal.fire({
        ...swalConfig,
        icon: 'error',
        title: 'Error',
        html: `
          <div style="text-align: center;">
            <p style="color: #9ca3af; margin: 0;">No NFT selected for listing</p>
          </div>
        `,
        confirmButtonText: 'OK'
      });
      return;
    }
    
    // Show loading state
    Swal.fire({
      ...swalConfig,
      title: '⏳ Processing Transaction',
      html: `
        <div style="text-align: center;">
          <div class="swal2-loader" style="border: 4px solid rgba(59, 130, 246, 0.3); border-top-color: #3b82f6; border-radius: 50%; width: 48px; height: 48px; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
          <p style="color: #9ca3af; margin: 0;">Please confirm the transaction in your wallet...</p>
        </div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
    
    try {
      const txHash = await listNFTForSale(selectedNFTId, priceInput);
      
      // Update loading message with tx hash
      Swal.update({
        title: '⏳ Waiting for Confirmation',
        html: `
          <div style="text-align: center;">
            <div class="swal2-loader" style="border: 4px solid rgba(16, 185, 129, 0.3); border-top-color: #10b981; border-radius: 50%; width: 48px; height: 48px; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
            <p style="color: #9ca3af; margin: 0 0 12px 0;">Transaction submitted! Waiting for confirmation...</p>
            <div style="font-size: 11px; color: #6b7280; word-break: break-all; background: rgba(255, 255, 255, 0.1); padding: 8px; border-radius: 6px; margin-top: 8px;">
              ${txHash}
            </div>
          </div>
        `
      });

      // Wait for transaction confirmation
      // If confirmation check fails, we still show success since transaction was submitted
      let receipt = null;
      try {
        receipt = await waitForTransaction(txHash, 1, 180000); // 3 minutes timeout
      } catch (waitError) {
        // If waiting fails but we have a tx hash, transaction was still submitted
        // This can happen if RPC is slow or transaction takes longer than expected
        // We'll show success with a note that confirmation is pending
        console.warn('Transaction confirmation check issue, but transaction was submitted:', txHash);
      }

      // Show success
      Swal.fire({
        ...swalConfig,
        icon: 'success',
        title: '📋 Listed Successfully!',
        html: `
          <div style="text-align: center;">
            <div style="font-size: 48px; margin-bottom: 16px;">✅</div>
            <div style="font-size: 20px; font-weight: bold; color: #10b981; margin-bottom: 8px;">
              NFT Listed for Sale!
            </div>
            <div style="font-size: 16px; margin-bottom: 12px; color: #d1d5db;">
              Your NFT #${selectedNFTId} is now listed for ${priceInput} ETCPOWv2
            </div>
            <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; margin-top: 16px;">
              Transaction Hash:
            </div>
            <div style="font-size: 11px; color: #9ca3af; word-break: break-all; background: rgba(255, 255, 255, 0.1); padding: 8px; border-radius: 6px;">
              ${txHash}
            </div>
          </div>
        `,
        confirmButtonText: 'Great!',
        confirmButtonColor: '#10b981'
      });
      handleCloseModal();
      await loadBlockchainData();
    } catch (error) {
      console.error('Error listing NFT:', error);
      
      if (isUserRejection(error)) {
        Swal.fire({
          ...swalConfig,
          icon: 'info',
          title: 'Transaction Cancelled',
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; margin-bottom: 16px;">🚫</div>
              <p style="color: #9ca3af; margin: 0;">You cancelled the transaction in your wallet.</p>
            </div>
          `,
          confirmButtonText: 'OK'
        });
      } else {
        Swal.fire({
          ...swalConfig,
          icon: 'error',
          title: '❌ Listing Failed',
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
              <p style="color: #ef4444; margin: 0 0 8px 0; font-weight: 600;">Transaction Failed</p>
              <p style="color: #9ca3af; margin: 0; font-size: 14px;">${error.message || 'Failed to list NFT for sale. Please try again.'}</p>
            </div>
          `,
          confirmButtonText: 'Try Again'
        });
      }
    }
  };

  const claimLegacyLicensesHandler = async (amount) => {
    // Show loading state
    const loadingSwal = Swal.fire({
      ...swalConfig,
      title: '⏳ Processing Transaction',
      html: `
        <div style="text-align: center;">
          <div class="swal2-loader" style="border: 4px solid rgba(251, 146, 60, 0.3); border-top-color: #fb923c; border-radius: 50%; width: 48px; height: 48px; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
          <p style="color: #9ca3af; margin: 0;">Please confirm the transaction in your wallet...</p>
        </div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      // Get transaction hash
      const txHash = await claimLegacyLicenses(amount);
      
      // Update loading message with tx hash
      Swal.update({
        title: '⏳ Waiting for Confirmation',
        html: `
          <div style="text-align: center;">
            <div class="swal2-loader" style="border: 4px solid rgba(16, 185, 129, 0.3); border-top-color: #10b981; border-radius: 50%; width: 48px; height: 48px; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
            <p style="color: #9ca3af; margin: 0 0 12px 0;">Transaction submitted! Waiting for confirmation...</p>
            <div style="font-size: 11px; color: #6b7280; word-break: break-all; background: rgba(255, 255, 255, 0.1); padding: 8px; border-radius: 6px; margin-top: 8px;">
              ${txHash}
            </div>
          </div>
        `
      });

      // Wait for transaction confirmation
      // If confirmation check fails, we still show success since transaction was submitted
      let receipt = null;
      try {
        receipt = await waitForTransaction(txHash, 1, 180000); // 3 minutes timeout
      } catch (waitError) {
        // If waiting fails but we have a tx hash, transaction was still submitted
        // This can happen if RPC is slow or transaction takes longer than expected
        // We'll show success with a note that confirmation is pending
        console.warn('Transaction confirmation check issue, but transaction was submitted:', txHash);
      }

      // Show success
      Swal.fire({
        ...swalConfig,
        icon: 'success',
        title: '🎉 Claim Successful!',
        html: `
          <div style="text-align: center;">
            <div style="font-size: 48px; margin-bottom: 16px;">🎫</div>
            <div style="font-size: 20px; font-weight: bold; color: #10b981; margin-bottom: 8px;">
              Successfully Claimed!
            </div>
            <div style="font-size: 16px; margin-bottom: 12px; color: #d1d5db;">
              ${amount} license(s) have been added to your wallet
            </div>
            <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; margin-top: 16px;">
              Transaction Hash:
            </div>
            <div style="font-size: 11px; color: #9ca3af; word-break: break-all; background: rgba(255, 255, 255, 0.1); padding: 8px; border-radius: 6px;">
              ${txHash}
            </div>
          </div>
        `,
        confirmButtonText: 'Awesome!',
        confirmButtonColor: '#10b981'
      });
      setClaimModalOpen(false);
      setClaimAmount('');
      await loadBlockchainData();
    } catch (error) {
      console.error('Error claiming licenses:', error);
      
      // Check if user rejected
      if (isUserRejection(error)) {
        Swal.fire({
          ...swalConfig,
          icon: 'info',
          title: 'Transaction Cancelled',
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; margin-bottom: 16px;">🚫</div>
              <p style="color: #9ca3af; margin: 0;">You cancelled the transaction in your wallet.</p>
            </div>
          `,
          confirmButtonText: 'OK'
        });
      } else {
        Swal.fire({
          ...swalConfig,
          icon: 'error',
          title: '❌ Claim Failed',
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
              <p style="color: #ef4444; margin: 0 0 8px 0; font-weight: 600;">Transaction Failed</p>
              <p style="color: #9ca3af; margin: 0; font-size: 14px;">${error.message || 'Failed to claim licenses. Please try again.'}</p>
            </div>
          `,
          confirmButtonText: 'Try Again'
        });
      }
    }
  };

  const publicMintLicensesHandler = async (tier, amount) => {
    // Show loading state
    const loadingSwal = Swal.fire({
      ...swalConfig,
      title: '⏳ Processing Transaction',
      html: `
        <div style="text-align: center;">
          <div class="swal2-loader" style="border: 4px solid rgba(59, 130, 246, 0.3); border-top-color: #3b82f6; border-radius: 50%; width: 48px; height: 48px; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
          <p style="color: #9ca3af; margin: 0;">Please confirm the transaction in your wallet...</p>
        </div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    let txHash = null;
    try {
      // Get transaction hash
      txHash = await publicMintLicenses(tier, amount);
      
      // Update loading message with tx hash
      Swal.update({
        title: '⏳ Waiting for Confirmation',
        html: `
          <div style="text-align: center;">
            <div class="swal2-loader" style="border: 4px solid rgba(16, 185, 129, 0.3); border-top-color: #10b981; border-radius: 50%; width: 48px; height: 48px; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
            <p style="color: #9ca3af; margin: 0 0 12px 0;">Transaction submitted! Waiting for confirmation...</p>
            <div style="font-size: 11px; color: #6b7280; word-break: break-all; background: rgba(255, 255, 255, 0.1); padding: 8px; border-radius: 6px; margin-top: 8px;">
              ${txHash}
            </div>
          </div>
        `
      });

      // Wait for transaction confirmation
      // If confirmation check fails, we still show success since transaction was submitted
      let receipt = null;
      try {
        receipt = await waitForTransaction(txHash, 1, 180000); // 3 minutes timeout
      } catch (waitError) {
        // If waiting fails but we have a tx hash, transaction was still submitted
        // This can happen if RPC is slow or transaction takes longer than expected
        // We'll show success with a note that confirmation is pending
        console.warn('Transaction confirmation check issue, but transaction was submitted:', txHash);
      }

      // Show success message
      // If we have a txHash, transaction was submitted successfully
      Swal.fire({
        ...swalConfig,
        icon: 'success',
        title: '🎉 Mint Successful!',
        html: `
          <div style="text-align: center;">
            <div style="font-size: 48px; margin-bottom: 16px;">🪙</div>
            <div style="font-size: 20px; font-weight: bold; color: #00ff88; margin-bottom: 8px;">
              Successfully Minted!
            </div>
            <div style="font-size: 16px; margin-bottom: 20px; color: #d1d5db;">
              ${amount} license(s) have been minted and added to your wallet
            </div>
            <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px;">
              Transaction Hash:
            </div>
            <div style="font-size: 11px; color: #9ca3af; word-break: break-all; background: rgba(255, 255, 255, 0.1); padding: 8px; border-radius: 6px; margin-bottom: 16px;">
              ${txHash}
            </div>
            <a href="https://blockscout.com/etc/mainnet/tx/${txHash}" target="_blank" rel="noopener noreferrer" style="display: inline-block; color: #00ff88; text-decoration: none; font-size: 13px; padding: 8px 16px; border: 1px solid rgba(0, 255, 136, 0.3); border-radius: 8px; transition: all 0.2s; background: rgba(0, 255, 136, 0.1);">
              View on BlockScout →
            </a>
          </div>
        `,
        confirmButtonText: 'Awesome!',
        confirmButtonColor: '#00ff88'
      });

      setMintModalOpen(false);
      setMintAmount('');
      await loadBlockchainData();
    } catch (error) {
      console.error('Error minting licenses:', error);
      
      // Check if it's a "transaction not found" error - this means transaction was submitted but not confirmed yet
      const errorMsg = (error.message || '').toLowerCase();
      const errorName = (error.name || '').toLowerCase();
      const isTransactionNotFound = 
        errorMsg.includes('transactionnotfound') ||
        errorMsg.includes('transaction not found') ||
        errorName === 'transactionnotfound';
      
      // If transaction was submitted (we have txHash) but confirmation check failed, show success
      // This happens when RPC is slow but transaction was successfully submitted
      if (isTransactionNotFound && txHash) {
        Swal.fire({
          ...swalConfig,
          icon: 'success',
          title: '🎉 Mint Successful!',
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; margin-bottom: 16px;">🪙</div>
              <div style="font-size: 20px; font-weight: bold; color: #00ff88; margin-bottom: 8px;">
                Successfully Minted!
              </div>
              <div style="font-size: 16px; margin-bottom: 20px; color: #d1d5db;">
                ${amount} license(s) have been minted and added to your wallet
              </div>
              <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px;">
                Transaction Hash:
              </div>
              <div style="font-size: 11px; color: #9ca3af; word-break: break-all; background: rgba(255, 255, 255, 0.1); padding: 8px; border-radius: 6px; margin-bottom: 16px;">
                ${txHash}
              </div>
              <a href="https://blockscout.com/etc/mainnet/tx/${txHash}" target="_blank" rel="noopener noreferrer" style="display: inline-block; color: #00ff88; text-decoration: none; font-size: 13px; padding: 8px 16px; border: 1px solid rgba(0, 255, 136, 0.3); border-radius: 8px; transition: all 0.2s; background: rgba(0, 255, 136, 0.1);">
                View on BlockScout →
              </a>
            </div>
          `,
          confirmButtonText: 'Awesome!',
          confirmButtonColor: '#00ff88'
        });
        setMintModalOpen(false);
        setMintAmount('');
        await loadBlockchainData();
        return;
      }
      
      // Check if user rejected
      if (isUserRejection(error)) {
        Swal.fire({
          ...swalConfig,
          icon: 'info',
          title: 'Transaction Cancelled',
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; margin-bottom: 16px;">🚫</div>
              <p style="color: #9ca3af; margin: 0;">You cancelled the transaction in your wallet.</p>
            </div>
          `,
          confirmButtonText: 'OK'
        });
      } else {
        Swal.fire({
          ...swalConfig,
          icon: 'error',
          title: '❌ Mint Failed',
          html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
              <p style="color: #ef4444; margin: 0 0 8px 0; font-weight: 600;">Transaction Failed</p>
              <p style="color: #9ca3af; margin: 0; font-size: 14px;">${error.message || 'Failed to mint licenses. Please try again.'}</p>
            </div>
          `,
          confirmButtonText: 'Try Again'
        });
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-dark-bg text-text-primary">
      {isLoading && (
        <div className="fixed inset-0 bg-dark-bg/90 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-etc-primary border-t-transparent rounded-full animate-spin mx-auto mb-4 shadow-lg shadow-etc-primary/20"></div>
            <p className="text-text-primary text-lg font-medium">Loading Marketplace...</p>
          </div>
        </div>
      )}

      <Header
        account={account}
        isConnected={isConnected}
        isConnecting={isConnecting}
        onConnectWallet={handleConnectWallet}
        onDisconnectWallet={disconnect}
        onToggleMode={toggleMode}
        mode={mode}
        onSortByPriceAscending={sortByPriceAscending}
        onSortByPriceDescending={sortByPriceDescending}
        onRequestApproval={requestApproval}
        onShowHowTo={showHowTo}
        onOpenClaimModal={() => setClaimModalOpen(true)}
        onOpenMintModal={() => setMintModalOpen(true)}
        featureFlags={featureFlags}
      />

      <main className="container mx-auto flex-1 flex flex-col pt-12 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
          <div className="flex items-center gap-4 flex-wrap">
            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-etc-primary via-blue-500 to-purple-600 bg-clip-text text-transparent tracking-tight">
              {mode === 'All' ? '🌟 All Listed NFTs' : '🏠 My NFTs'}
            </h2>
            <div className="flex items-center gap-2 px-3 py-0.5 bg-etc-primary/20 border border-etc-primary/30 rounded-full">
              <div className="w-2 h-2 bg-etc-primary rounded-full animate-pulse"></div>
              <span className="text-etc-primary text-sm font-medium">Live</span>
            </div>
          </div>
          <div className="flex items-center">
            <div className="bg-dark-surface/80 backdrop-blur-sm border border-dark-border px-4 py-1 rounded-full shadow-lg">
              <span className="text-text-primary font-semibold">
                {mode === 'My' 
                  ? nfts.filter(nft => nft.owner?.toLowerCase() === account?.toLowerCase() || nft.seller?.toLowerCase() === account?.toLowerCase()).length
                  : nfts.filter(nft => nft.forSale === true || nft.price !== '0').length
                }
              </span>
              <span className="text-text-muted text-sm ml-2">items</span>
            </div>
          </div>
        </div>

        <div className="flex-1" style={{ position: 'relative', minHeight: '400px', paddingBottom: '120px' }}>
          <NFTGrid
            nfts={nfts}
            account={account}
            mode={mode}
            onBuyNFT={handleBuyNFT}
            onListForSale={handleListForSale}
            onCancelListing={handleCancelListing}
            onViewDetails={handleViewDetails}
            onRenewSuccess={handleRenewSuccess}
          />
        </div>
      </main>

      <Footer className="mt-auto" style={{ marginTop: '80px' }} />

      {/* Modals */}
      <ListForSaleModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        priceInput={priceInput}
        setPriceInput={setPriceInput}
        onSubmit={handleSubmitPrice}
        minPrice={minListingPrice}
      />

      <LegacyClaimsModal
        isOpen={claimModalOpen}
        onClose={() => setClaimModalOpen(false)}
        claimAmount={claimAmount}
        setClaimAmount={setClaimAmount}
        legacyInfo={legacyInfo}
        legacyClaimPrice={legacyClaimPrice}
        etcPowBalance={etcPowBalance}
        onClaim={claimLegacyLicensesHandler}
        onFetchLegacyInfo={async () => {
          const legacyData = await getLegacyInfo();
          setLegacyInfo(legacyData);
          return legacyData;
        }}
        onFetchClaimPrice={async () => {
          const price = await getLegacyClaimPrice();
          setLegacyClaimPrice(price);
          return price;
        }}
        onFetchBalance={async () => {
          const balance = await getETCPOWBalance();
          setEtcPowBalance(balance);
          return balance;
        }}
      />

      <MintLicensesModal
        isOpen={mintModalOpen}
        onClose={() => setMintModalOpen(false)}
        mintAmount={mintAmount}
        setMintAmount={setMintAmount}
        publicPrices={publicPrices}
        tierEnabled={tierEnabled}
        onMint={publicMintLicensesHandler}
      />

      <WalletSelectionModal
        isOpen={walletModalOpen}
        onClose={() => setWalletModalOpen(false)}
        onSelectWallet={handleSelectWallet}
      />
    </div>
  );
}

export default App;
